// src/hooks/useAuth.js
import { useState, useEffect, useContext, createContext } from 'react';
import { verifyToken } from '../services/api/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          // Verify token is still valid and get fresh user data
          try {
            const response = await verifyToken();
            
            // Use fresh data from verify endpoint (includes updated permissions and subscription)
            setToken(storedToken);
            setUser(response.user);
            setIsAuthenticated(true);
            
            // Update stored user data with fresh data
            localStorage.setItem('user', JSON.stringify(response.user));
          } catch (error) {
            // Token is invalid, clear storage
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);
    
    // Store in localStorage
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    
    // Redirect to login
    window.location.href = '/login';
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    setUser(newUserData);
    localStorage.setItem('user', JSON.stringify(newUserData));
  };

  const refreshUser = async () => {
    try {
      const response = await verifyToken();
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      return response.user;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  // Helper functions to access nested user data
  const getUserRole = () => {
    return user?.role?.name || null;
  };

  const getUserPermissions = () => {
    return user?.role?.permissions || [];
  };

  const getUserBusiness = () => {
    return user?.business || null;
  };

  const getBusinessName = () => {
    return user?.business?.name || null;
  };

  // Subscription helper functions
  const getSubscription = () => {
    return user?.subscription || null;
  };

  const hasActiveSubscription = () => {
    return user?.hasActiveSubscription || false;
  };

  const getSubscriptionStatus = () => {
    return user?.subscription?.status || null;
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    refreshUser,
    getUserRole,
    getUserPermissions,
    getUserBusiness,
    getBusinessName,
    getSubscription,
    hasActiveSubscription,
    getSubscriptionStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};